import { useBrandLocale } from '../../hooks/useBrandLocale';
import { faWallet } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  StatCard,
  StatGrid,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../components/ui';
import { useMyServicePayoutsQuery } from '../../services/serviceService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { payoutBatchState, payoutMoney, payoutWeekLabel } from './payouts';
import { PROVIDER_TAX_INFO_PATH } from './servicePaths';

/**
 * The Provider's earnings (spec 019 W20, D23): confirmed jobs waiting for next
 * Monday's batch, and every weekly batch with whether it was sent and its
 * transfer reference.
 */
export default function PayoutsPage() {
  // Spec 028: payouts are in the brand's currency.
  const { currency } = useBrandLocale();
  const { data, loading, error } = useMyServicePayoutsQuery({ fetchPolicy: 'cache-and-network' });
  const payouts = data?.myServicePayouts ?? null;

  return (
    <PageContainer width="lg" canvas data-testid="provider-payouts">
      <PageHeader
        eyebrow="Provider"
        title="Payouts"
        description="We pay each week's confirmed jobs by bank transfer the week after. Refunds come off the week they are decided."
      />
      <Stack>
        {loading && !payouts && <LoadingBlock label="Loading payouts" />}
        {error && !payouts && (
          <Alert tone="error" title="Could not load payouts">
            {stayErrorMessage(error)}
          </Alert>
        )}
        {payouts && (
          <>
            <StatGrid>
              <StatCard
                label="Waiting for the next payout"
                value={payoutMoney(payouts.pendingAmount, currency)}
                hint={`${payouts.pendingJobCount} confirmed job${payouts.pendingJobCount === 1 ? '' : 's'}`}
                data-testid="payouts-pending"
              />
              <StatCard
                label="Paid so far"
                value={payoutMoney(
                  payouts.batches.filter(b => b.paidAt).reduce((sum, b) => sum + b.amount, 0),
                  currency,
                )}
                hint={`${payouts.batches.filter(b => b.paidAt).length} payouts`}
              />
            </StatGrid>
            <Muted $size="sm">
              Keep your <Link to={PROVIDER_TAX_INFO_PATH}>tax information</Link> up to date. We use
              it for your yearly 1099.
            </Muted>
            <Card>
              <CardHeader title="Weekly payouts" />
              {payouts.batches.length === 0 ? (
                <EmptyState
                  icon={faWallet}
                  title="No payouts yet"
                  description="Your first payout is made the Monday after a customer confirms a job."
                />
              ) : (
                <TableWrap>
                  <Table data-testid="payouts-table">
                    <THead>
                      <TR>
                        <TH>Week</TH>
                        <TH $align="right">Jobs</TH>
                        <TH $align="right">Amount</TH>
                        <TH>Status</TH>
                        <TH>Reference</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {payouts.batches.map(batch => {
                        const state = payoutBatchState(batch);
                        return (
                          <TR key={batch.id}>
                            <TD>{payoutWeekLabel(batch.periodStart, batch.periodEnd)}</TD>
                            <TD $align="right">{batch.jobCount}</TD>
                            <TD $align="right">{payoutMoney(batch.amount, currency)}</TD>
                            <TD>
                              <Badge tone={state.tone}>{state.label}</Badge>
                              {batch.paidAt && (
                                <Muted $size="sm" style={{ display: 'block' }}>
                                  {DateTime.fromISO(batch.paidAt).toFormat('LLL d, yyyy')}
                                </Muted>
                              )}
                            </TD>
                            <TD>{batch.reference ?? '—'}</TD>
                          </TR>
                        );
                      })}
                    </TBody>
                  </Table>
                </TableWrap>
              )}
            </Card>
          </>
        )}
      </Stack>
    </PageContainer>
  );
}
